import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";

/**
 * Supabase Auth wrapper. Magic-link only; no passwords.
 *
 * Config is fetched at runtime from /admin/api/public/config so we don't
 * have to rebake the SPA when keys rotate. Build-time VITE_SUPABASE_*
 * envs still work as a fallback for local dev.
 */

interface PublicConfig {
  supabase_url: string;
  supabase_anon_key: string;
  allowed_domain: string;
  dev_auth_bypass: boolean;
  build_sha: string;
}

let _config: PublicConfig | null = null;
let _configPromise: Promise<PublicConfig> | null = null;

const FALLBACK_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const FALLBACK_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
const BUILD_DEV_BYPASS = !!import.meta.env.VITE_DEV_AUTH_BYPASS;

export const ALLOWED_DOMAIN = "@ultraviolet.club";

export async function loadConfig(): Promise<PublicConfig> {
  if (_config) return _config;
  if (_configPromise) return _configPromise;

  _configPromise = (async () => {
    try {
      const r = await fetch("/admin/api/public/config", { credentials: "omit" });
      if (r.ok) {
        const data = (await r.json()) as PublicConfig;
        _config = {
          supabase_url: data.supabase_url || FALLBACK_URL || "",
          supabase_anon_key: data.supabase_anon_key || FALLBACK_KEY || "",
          allowed_domain: data.allowed_domain || ALLOWED_DOMAIN,
          dev_auth_bypass: data.dev_auth_bypass || BUILD_DEV_BYPASS,
          build_sha: data.build_sha || "dev",
        };
        return _config;
      }
    } catch (err) {
      console.warn("[auth] /public/config fetch failed; using build-time fallbacks", err);
    }
    _config = {
      supabase_url: FALLBACK_URL || "",
      supabase_anon_key: FALLBACK_KEY || "",
      allowed_domain: ALLOWED_DOMAIN,
      dev_auth_bypass: BUILD_DEV_BYPASS,
      build_sha: "dev",
    };
    return _config;
  })();

  return _configPromise;
}

export function getConfigSync(): PublicConfig | null {
  return _config;
}

export const DEV_AUTH_BYPASS = BUILD_DEV_BYPASS; // legacy import shim — prefer config.dev_auth_bypass

let _client: SupabaseClient | null = null;

/** Call once at app boot, before any component renders. Loads /public/config
 * and primes the Supabase client. After this returns, getSupabase() works
 * synchronously. */
export async function initAuth(): Promise<PublicConfig> {
  const cfg = await loadConfig();
  if (!cfg.dev_auth_bypass && cfg.supabase_url && cfg.supabase_anon_key && !_client) {
    _client = createClient(cfg.supabase_url, cfg.supabase_anon_key, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: "kitt-admin-auth",
        flowType: "pkce",
      },
    });
  }
  return cfg;
}

/** Synchronous accessor. Returns null in dev-bypass or before initAuth() resolves. */
export function getSupabase(): SupabaseClient | null {
  return _client;
}

export function isAllowedEmail(email: string): boolean {
  const domain = _config?.allowed_domain ?? ALLOWED_DOMAIN;
  return email.toLowerCase().endsWith(domain);
}

export interface AuthUser {
  email: string;
  /** "owner" | "team" | "readonly" — derived in DEV bypass; comes from JWT claim otherwise. */
  role: "owner" | "team" | "readonly";
  display_name?: string;
  avatar_url?: string;
}

export function userFromSession(session: Session | null): AuthUser | null {
  if (!session) return null;
  const email = session.user?.email;
  if (!email) return null;
  const role = (session.user.app_metadata?.role as AuthUser["role"]) ?? "team";
  return {
    email,
    role,
    display_name: (session.user.user_metadata?.full_name as string) ?? email,
    avatar_url: session.user.user_metadata?.avatar_url as string | undefined,
  };
}

/** DEV bypass user — only used when dev_auth_bypass is on. */
export const DEV_USER: AuthUser = {
  email: "dev@ultraviolet.club",
  role: "owner",
  display_name: "Dev (bypass)",
};

export async function sendMagicLink(email: string): Promise<{ error: string | null }> {
  const supabase = getSupabase();
  if (!supabase) return { error: "Supabase not configured." };
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: window.location.origin + "/admin/",
      shouldCreateUser: true,
    },
  });
  return { error: error?.message ?? null };
}

export async function signOut() {
  const supabase = getSupabase();
  if (supabase) await supabase.auth.signOut();
}
